import { useEffect, useState } from "react";
import { Wallet, ArrowDownToLine, Loader2, X, Coins, Send } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFx } from "../lib/fx";

const REASON_LABELS = {
  booking_payout: "Booking payout",
  mint_earned: "Mint Points earned",
  mint_reversed: "Mint Points reversed",
  withdrawal: "Withdrawal",
  refund: "Refund",
  adjustment: "Adjustment",
};

export default function WalletPage() {
  const { user } = useAuth();
  const { currencies, format, byCode } = useFx();
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [error, setError] = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const reload = async () => {
    setError(null);
    try {
      const [b, t, w] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/wallet/transactions"),
        api.get("/wallet/withdrawals/me"),
      ]);
      setBalances(b.data.balances || []);
      setTransactions(t.data.items || []);
      setWithdrawals(w.data.items || []);
    } catch (e) { setError(formatErr(e)); }
  };

  useEffect(() => { if (user) reload(); }, [user?.id]);

  if (!user) return <div className="pt-32 px-6 text-muted">Sign in to view your wallet.</div>;

  return (
    <div className="pt-28 pb-24 px-5 md:px-10 mx-auto max-w-5xl">
      <div className="eyebrow mb-3 flex items-center gap-2">
        <span className={`role-badge ${user.role}`}>{user.role}</span> Wallet
      </div>
      <h1 className="section-title text-4xl md:text-5xl mb-2">Your wallet.</h1>
      <p className="text-sm text-muted mb-10 max-w-2xl">
        Balances credited from completed bookings, Mint Points earned, and any custom adjustments. Request a withdrawal in the currency of your choice — the concierge desk will process it.
      </p>

      {error && <div className="text-rose-300 text-sm mb-4" data-testid="wallet-error">{error}</div>}

      {/* Balances */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10" data-testid="wallet-balances">
        {balances.length === 0 ? (
          <div className="luxe-card p-6 text-center text-muted text-sm sm:col-span-2 lg:col-span-3">
            <Wallet className="mx-auto text-teal mb-2"/>
            No balances yet. Once a booking is completed, your wallet will fill up here.
          </div>
        ) : balances.map((b) => {
          const meta = byCode[b.currency];
          return (
            <div key={b.currency} className="luxe-card p-5" data-testid={`wallet-bal-${b.currency}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {meta?.icon_url ? (
                    <img src={meta.icon_url} alt="" className="h-6 w-6 rounded-full"/>
                  ) : (
                    <span className="h-6 w-6 rounded-full bg-teal/10 grid place-items-center text-teal text-[11px] font-mono">{meta?.symbol || b.currency[0]}</span>
                  )}
                  <div>
                    <div className="font-mono text-xs text-muted">{b.currency}</div>
                    <div className="text-[11px] text-muted/70">{meta?.name || "—"}</div>
                  </div>
                </div>
                <span className={cx("chip !text-[10px]", meta?.kind === "fiat" ? "" : meta?.kind === "crypto" ? "info" : "success")}>{meta?.kind}</span>
              </div>
              <div className="font-serif-display text-3xl mt-4">
                {format(b.balance, b.currency, { native: true })}
              </div>
              <div className="text-xs text-muted mt-1">
                ≈ {format(b.balance, b.currency)} <span className="text-muted/60">in your currency</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Withdraw button */}
      {balances.length > 0 && (
        <div className="mb-12">
          <button onClick={() => setShowWithdraw(true)} className="btn btn-primary" data-testid="btn-withdraw">
            <ArrowDownToLine size={14}/> Request withdrawal
          </button>
        </div>
      )}

      {/* Pending withdrawals */}
      {withdrawals.length > 0 && (
        <div className="mb-10">
          <h2 className="font-serif-display text-2xl mb-4">Withdrawal history</h2>
          <div className="space-y-2" data-testid="withdrawal-history">
            {withdrawals.map((w) => (
              <div key={w.id} className="luxe-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`wd-${w.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={cx("chip",
                      w.status === "paid" ? "success" :
                      w.status === "approved" ? "info" :
                      w.status === "rejected" ? "danger" : "warning")}>
                      {w.status}
                    </span>
                    <span className="text-xs text-muted font-mono">#{w.id.slice(0, 8)}</span>
                    <span className="text-xs text-muted">{new Date(w.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-serif-display text-lg">
                    {format(w.amount, w.currency, { native: true })}
                    {w.destination_currency !== w.currency && (
                      <span className="text-muted text-base"> → ~{format(w.estimated_payout, w.destination_currency, { native: true })}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted mt-1 truncate font-mono">→ {w.destination_address}</div>
                  {w.tx_reference && <div className="text-xs text-teal/80 mt-1 font-mono">tx: {w.tx_reference}</div>}
                  {w.admin_note && <div className="text-xs text-muted mt-1 italic">"{w.admin_note}"</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions ledger */}
      <h2 className="font-serif-display text-2xl mb-4">Recent activity</h2>
      {transactions.length === 0 ? (
        <div className="luxe-card p-6 text-center text-muted text-sm">No transactions yet.</div>
      ) : (
        <div className="space-y-2" data-testid="wallet-transactions">
          {transactions.map((tx) => (
            <div key={tx.id} className="luxe-card p-4 flex items-center justify-between gap-3" data-testid={`tx-${tx.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={cx("chip !text-[10px]", tx.kind === "credit" ? "success" : "danger")}>
                    {REASON_LABELS[tx.reason] || tx.reason}
                  </span>
                  <span className="text-[10px] text-muted/60 font-mono">{new Date(tx.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm text-muted truncate">{tx.description}</div>
              </div>
              <div className="text-right">
                <div className={cx("font-serif-display text-lg", tx.amount > 0 ? "text-teal" : "text-rose-300")}>
                  {tx.amount > 0 ? "+" : ""}{format(tx.amount, tx.currency, { native: true })}
                </div>
                <div className="text-[10px] text-muted/60 font-mono">balance: {format(tx.balance_after, tx.currency, { native: true })}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showWithdraw && (
        <WithdrawModal balances={balances} currencies={currencies} onClose={(refresh) => { setShowWithdraw(false); if (refresh) reload(); }}/>
      )}
    </div>
  );
}

function WithdrawModal({ balances, currencies, onClose }) {
  const { format, convert } = useFx();
  const [src, setSrc] = useState(balances[0]?.currency || "EUR");
  const [amount, setAmount] = useState("");
  const [destCcy, setDestCcy] = useState(balances[0]?.currency || "EUR");
  const [destKind, setDestKind] = useState("crypto");
  const [destAddr, setDestAddr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const srcBalance = balances.find((b) => b.currency === src)?.balance || 0;
  const numAmount = Number(amount) || 0;
  const insufficient = numAmount > srcBalance;
  const estPayout = srcBalance && numAmount && destCcy !== src ? convert(numAmount, src, destCcy) : numAmount;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/wallet/withdrawals", {
        amount: numAmount, currency: src,
        destination_currency: destCcy, destination_kind: destKind,
        destination_address: destAddr.trim(), note: note.trim() || null,
      });
      onClose(true);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="withdraw-modal" onClick={() => !busy && onClose(false)}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-lg max-h-[95svh] overflow-y-auto modal-panel p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow flex items-center gap-2 text-teal"><Send size={12}/> Withdraw</div>
            <h3 className="font-serif-display text-2xl mt-1">Request a withdrawal</h3>
            <p className="text-sm text-muted mt-1">Concierge desk reviews and processes within 24h.</p>
          </div>
          <button type="button" onClick={() => onClose(false)} className="text-muted hover:text-bone" data-testid="wd-close"><X size={20}/></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-tiny block mb-1">From wallet</span>
            <select value={src} onChange={(e) => { setSrc(e.target.value); setDestCcy(e.target.value); }} className="input-bare" data-testid="wd-src">
              {balances.map((b) => (<option key={b.currency} value={b.currency}>{b.currency} — {b.balance.toFixed(2)} avail</option>))}
            </select>
          </label>
          <label className="block">
            <span className="label-tiny block mb-1">Amount</span>
            <input type="number" min="0" step="0.01" max={srcBalance} value={amount} onChange={(e) => setAmount(e.target.value)} required className="input-bare" data-testid="wd-amount"/>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-tiny block mb-1">Pay me in</span>
            <select value={destCcy} onChange={(e) => setDestCcy(e.target.value)} className="input-bare" data-testid="wd-dest-ccy">
              {currencies.filter((c) => c.kind !== "points").map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label-tiny block mb-1">Destination type</span>
            <select value={destKind} onChange={(e) => setDestKind(e.target.value)} className="input-bare" data-testid="wd-dest-kind">
              <option value="crypto">Crypto wallet</option>
              <option value="fiat">Bank / IBAN</option>
              <option value="internal">Internal transfer</option>
            </select>
          </label>
        </div>

        <label className="block">
          <span className="label-tiny block mb-1">Wallet address / IBAN / email</span>
          <input value={destAddr} onChange={(e) => setDestAddr(e.target.value)} required minLength={4} className="input-bare font-mono text-xs" placeholder="0x… or DE89… or you@bank.com" data-testid="wd-dest-addr"/>
        </label>

        <label className="block">
          <span className="label-tiny block mb-1">Note (optional)</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="input-bare resize-y" data-testid="wd-note"/>
        </label>

        {numAmount > 0 && destCcy !== src && (
          <div className="luxe-card p-3 text-sm">
            Estimated payout: <span className="font-serif-display text-lg">~{format(estPayout, destCcy, { native: true })}</span>
            <div className="text-[11px] text-muted">Final amount uses the FX rate at the moment of payout.</div>
          </div>
        )}
        {insufficient && <div className="text-rose-300 text-xs">Amount exceeds available balance.</div>}
        {error && <div className="text-rose-300 text-sm">{error}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onClose(false)} className="btn btn-ghost" data-testid="wd-cancel">Cancel</button>
          <button type="submit" disabled={busy || insufficient || !numAmount || !destAddr} className="btn btn-primary" data-testid="wd-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <ArrowDownToLine size={14}/>} Submit request
          </button>
        </div>
      </form>
    </div>
  );
}
